import React, { FC, ReactElement } from 'react'
import { API_URL, NETWORK } from '@/utils/constants'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import FeeSlider from '@/components/fee-slider'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import Button from '@/components/button'
import { formatRune } from '@/utils/format-rune'
import axios from 'axios'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import useDebounce from '@/utils/hooks/useDebounce'
import Link from 'next/link'
import Slider from '@mui/material/Slider'
import classNames from 'classnames'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'

import styles from '@/components/fee-slider/FeeSlider.module.css'

const BRC20Transfer: FC<{
	ticker: string
	balance: any
	onComplete: () => void
}> = (props): ReactElement => {
	const { ticker, balance: _balance, onComplete } = props
	const [feeRate, setFeeRate] = React.useState<any>(null)
	const [fee, setFee] = React.useState(0)
	const [psbt, setPsbt] = React.useState<any>(null)
	const [mintPsbts, setMintPsbts] = React.useState<any[]>([])
	const [error, setError] = React.useState<any>(null)
	const loadingFeeRef = React.useRef(false)
	const [loading, setLoading] = React.useState(false)
	const { exchangeRate } = useExchangeRate()
	const feePrice = useSatoshiPrice(fee)

	const { data } = useSwr(`${API_URL}/rune/${ticker}/stats`, fetcher)

	const handleSubmit = React.useCallback(async () => {
		setLoading(true)

		try {
			const rawtxs = await Wallet.signPSBTSExtractTx([psbt].concat(mintPsbts))

			// Every mint spends the setup by the id the server computed before
			// signing. If signing moved it (nested-segwit or legacy funder), the
			// setup would broadcast and orphan every mint — abort with zero sent.
			for (let i = 1; i < rawtxs.length; i++) {
				Wallet.assertChainLinks(`rune mint ${i}`, rawtxs[0], rawtxs[i])
			}

			//const data = ['asdf']
			const { data } = await axios.post(`${API_URL}/wallet/broadcast-bulk`, { rawtxs: rawtxs })

			// @ts-ignore
			if (data.error) {
				// @ts-ignore
				setError(data.error.message)
			} else if (!data[0]) {
				setError('Failed to broadcast' as any)
			} else {
				event.emit('open-broadcast-modal', { event: 'success', txid: data[0] })
				onComplete()
			}
		} catch (e) {
			setError((e as any).message || e)
		}

		setLoading(false)
	}, [psbt, mintPsbts, onComplete])

	const [count, setCount] = React.useState(10)
	const handleChangeCount = React.useCallback((evt: any) => setCount(evt.target.value), [])

	const debouncedFeeRate: number = useDebounce<number>(feeRate as any, 300)
	const debouncedCount: number = useDebounce<number>(count, 300)

	const disabled = React.useMemo(() => !data || loading || !psbt, [data, loading, psbt])

	React.useEffect(() => {
		async function run() {
			loadingFeeRef.current = true
			setError(true as any)
			setPsbt(null)
			setMintPsbts([])
			setFee(0)

			if (data && debouncedFeeRate) {
				try {
					const { setup, mints, fee } = await Wallet.getRuneMint(
						data.id,
						debouncedCount,
						debouncedFeeRate
					)
					setFee(Number(fee))
					setPsbt(setup as any)
					setMintPsbts(mints as any)
				} catch (e) {
					setError(
						(e as any)?.response?.data?.message || (e as any).message || (e as any).toString()
					)
				}
			}

			loadingFeeRef.current = false
		}

		run()
	}, [data, debouncedFeeRate, debouncedCount])

	return (
		<div>
			<div>
				<div className="flex items-center justify-between">
					<p>Mint Amount: {formatRune((data.mint_amount || 1) * count, data)}</p>
					<p>{count}</p>
				</div>
				<Slider
					value={count}
					min={1}
					max={24}
					step={1}
					onChange={handleChangeCount}
					className={styles.slider}
					classes={{
						root: classNames(styles.slider)
					}}
				/>
			</div>
			<div className="border border-border rounded-lg p-3">
				<FeeSlider feeRate={feeRate} setFeeRate={setFeeRate} />
				<div className="flex items-center justify-between mt-4">
					<p className="text-sm font-bold text-foreground/80">Service Fee</p>
					<div className="flex flex-col justify-right items-right">
						<p className="text-xs font-bold text-right">1 Mint</p>
						<p className="text-sm font-regular text-muted-foreground text-right">
							{formatRune(data?.mint_amount || 1, data)}
						</p>
					</div>
				</div>
				<div className="flex items-center justify-between mt-4">
					<p className="text-sm font-bold text-foreground/80">Transaction Fee</p>
					<div className="flex flex-col justify-right items-right">
						<p className="text-xs font-bold text-right">
							{feePrice} {NETWORK}
						</p>
						<p className="text-sm font-regular text-muted-foreground text-right">
							${((fee / 1e8) * exchangeRate).toFixed(2)}
						</p>
					</div>
				</div>
			</div>
			{error && <p className="mt-4 text-red-500 text-xs">{error}</p>}
			<div className="items-center text-muted-foreground text-xs mt-4">
				By clicking "mint now", you agree to the Ordinals Wallet{' '}
				<Link href={`/terms`} className="underline" target="_blank">
					Terms of Service.
				</Link>
			</div>
			<div className="mt-6">
				<ButtonGradientEpic
					disabled={disabled}
					onClick={handleSubmit}
					loading={loading}
					className="h-[48px] rounded-full w-full"
				>
					Mint Now
				</ButtonGradientEpic>
				<Button
					className="text-muted-foreground rounded-full text-base font-semibold p-0 h-12"
					onClick={onComplete}
				>
					Close
				</Button>
			</div>
		</div>
	)
}

export default BRC20Transfer
